import type { IntlShape } from 'react-intl'

export interface FlightTileBaggage {
  carryOn: boolean
  checked: boolean
  carryOnLabel?: string
  checkedLabel?: string
}

export type BaggageSummaryInclusion = Pick<App.LegInclusion, 'description' | 'extras'>

const carryOnTypes = new Set(['carry_on_bag', 'carry_on_baggage'])

function isIncluded(feature: Pick<App.FareFamilyFeature, 'status'>) {
  return feature.status === 'INCLUDED'
}

function isCarryOnType(type: App.FareFamilyFeature['type']) {
  return type !== undefined && carryOnTypes.has(type)
}

function getIncludedCarryOn<T extends Pick<App.FareFamilyFeature, 'type' | 'status' | 'description' | 'extras'>>(features: Array<T>) {
  return features.find(feature => isCarryOnType(feature.type) && isIncluded(feature))
}

function getIncludedCheckedBaggage<T extends Pick<App.FareFamilyFeature, 'type' | 'status' | 'description' | 'extras'>>(features: Array<T>) {
  return features.find(feature => feature.type === 'checked_baggage' && isIncluded(feature))
}

function getBaggageInformation(
  inclusion: BaggageSummaryInclusion,
  passengerType?: string,
): App.InclusionBaggageInformation | undefined {
  const items = inclusion.extras?.baggageInformation
  if (!Array.isArray(items) || items.length === 0) {
    return undefined
  }

  if (passengerType) {
    return items.find(item => item.passengerType === passengerType)
      ?? items.find(item => !item.passengerType)
  }

  const aggregate = items.find(item => !item.passengerType)
  if (aggregate) {
    return aggregate
  }

  const adult = items.find(item => item.passengerType === 'ADT')
  if (adult) {
    return adult
  }

  // Passenger-specific INF/CHD-only data is not representative for the general tile label.
  if (items.every(item => item.passengerType === 'INF' || item.passengerType === 'CHD')) {
    return undefined
  }

  return items[0]
}

function formatBaggageQuantityWeightLabel(
  quantity: number | undefined,
  unitValue: number,
  unit: string,
): string {
  const weight = `${unitValue}${unit.toLowerCase()}`
  if (quantity != null && quantity > 1) {
    return `${quantity}×${weight}`
  }

  return weight
}

export function formatBaggageSummaryLabel(
  inclusion: BaggageSummaryInclusion,
  passengerType?: string,
): string | undefined {
  const info = getBaggageInformation(inclusion, passengerType)

  if (info?.unitValue && info?.unit) {
    const quantity = info.quantity != null && info.quantity > 1 ? info.quantity : undefined
    return formatBaggageQuantityWeightLabel(quantity, info.unitValue, info.unit)
  }

  return undefined
}

export function getGenericBaggageSummaryLabel(
  baggageKind: 'carry_on' | 'checked',
  intl: IntlShape,
): string {
  if (baggageKind === 'carry_on') {
    return intl.formatMessage({
      id: 's3vzZzSn_wbWZQBRfs6V',
      defaultMessage: 'Carry-on',
      description: 'Label indicating carry-on baggage is included in flight fare',
    })
  }

  return intl.formatMessage({
    id: 'kxZtcahJ2tAn2awFzfd3',
    defaultMessage: 'Checked bag',
    description: 'Label indicating checked baggage is included in flight fare',
  })
}

export function composeBaggageDisplayLabel(categoryLabel: string, detailLabel?: string): string {
  if (!detailLabel) {
    return categoryLabel
  }

  return `${categoryLabel} ${detailLabel}`
}

export function getBaggageSummaryDisplayLabel(
  inclusion: BaggageSummaryInclusion,
  baggageKind: 'carry_on' | 'checked',
  intl: IntlShape,
  passengerType?: string,
): string {
  return composeBaggageDisplayLabel(
    getGenericBaggageSummaryLabel(baggageKind, intl),
    formatBaggageSummaryLabel(inclusion, passengerType),
  )
}

export function getFlightTileBaggageFromLeg(leg: App.Leg): FlightTileBaggage {
  const carryOn = getIncludedCarryOn(leg.inclusions)
  const checked = getIncludedCheckedBaggage(leg.inclusions)

  return {
    carryOn: Boolean(carryOn),
    checked: Boolean(checked),
    carryOnLabel: carryOn ? formatBaggageSummaryLabel(carryOn) : undefined,
    checkedLabel: checked ? formatBaggageSummaryLabel(checked) : undefined,
  }
}

export function getFlightTileBaggageFromJourney(journey: App.JourneyV2): FlightTileBaggage {
  const fareFeatures = journey.fareFamily?.fareFeatures ?? []
  const carryOn = getIncludedCarryOn(fareFeatures)
  const checked = getIncludedCheckedBaggage(fareFeatures)

  return {
    carryOn: Boolean(carryOn),
    checked: Boolean(checked),
    carryOnLabel: carryOn?.description,
    checkedLabel: checked?.description,
  }
}
